import { startTransition, useEffect, useRef, useState, type ClipboardEvent, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { normalizeOtpCode, OTP_LENGTH, WEB_ROUTES } from '@portal-contract';
import { Btn } from '@/components/Btn';
import { api } from '@/api/client';
import { ApiClientError } from '@/api/errors';
import { useI18n } from '@/i18n/I18nProvider';
import { defaultDestination, useSession } from '@/session/SessionProvider';
import { useLoginFlow } from '@/session/LoginFlow';
import { useCountdown } from '@/hooks/useCountdown';
import { Field } from '@/components/Field';
import { PageHeading } from '@/components/PageHeading';
import { Card } from '@/components/Card';
import { errorMessage } from '@/components/States';
import type { MessageKey } from '@/i18n/translate';

const digitsOnly = (s: string): string => s.replace(/\D+/g, '').slice(0, OTP_LENGTH);

/**
 * `/login/code`: one numeric field (not six boxes: screen readers and paste both work better),
 * paste of "123 456" or "123-456" accepted, resend with a countdown, plain wording for wrong,
 * expired and locked codes. Wording never says whether the address exists (plan 4.3).
 */
export function LoginCodeScreen() {
  const flow = useLoginFlow();
  if (!flow.email && !flow.codeOnly) return <Navigate to={WEB_ROUTES.login} replace />;
  return <CodeStep email={flow.email} />;
}

/** `email` is null in "I already have a code" mode: no request was made here, so no address is known. */
function CodeStep({ email }: { email: string | null }) {
  const { t, lang } = useI18n();
  const session = useSession();
  const flow = useLoginFlow();
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [resending, setResending] = useState(false);
  // After an expired or dead code the old challenge cannot be resent: ask for a brand new one.
  const [needsFresh, setNeedsFresh] = useState(false);
  const cooldown = useCountdown(flow.cooldownUntil);

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  const show = (key: MessageKey, vars?: Record<string, number | string>) => setError(t(key, vars));

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text');
    if (!pasted) return;
    e.preventDefault();
    const value = digitsOnly(pasted);
    setCode(value);
    setError(null);
    // A whole code pasted in signs in straight away; a partial paste waits for the rest.
    if (normalizeOtpCode(value)) void verify(value);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void verify(code);
  }

  async function verify(value: string) {
    if (pending) return;
    const digits = normalizeOtpCode(value);
    if (!digits) {
      show('code.format');
      inputRef.current?.focus();
      return;
    }
    setError(null);
    setNotice(null);
    setPending(true);
    try {
      const verified = await api.verifyCode(digits);
      await session.signIn(verified);
      const from = (location.state as { from?: string } | null)?.from;
      const allowed = from && verified.customers.some((c) => from === `/a/${c.id}` || from.startsWith(`/a/${c.id}/`));
      // One transition: clearing the typed address on its own would make this screen redirect to /login first.
      startTransition(() => {
        flow.clear();
        navigate(allowed ? from : defaultDestination(verified.customers), { replace: true });
      });
    } catch (err) {
      if (session.handleSessionError(err)) return;
      if (err instanceof ApiClientError && err.code === 'invalid_code') {
        if (err.attemptsRemaining !== undefined) show('code.invalid', { n: err.attemptsRemaining });
        else show('error.generic');
        setCode('');
      } else if (err instanceof ApiClientError && (err.code === 'code_expired' || err.code === 'no_challenge')) {
        show('code.expired');
        setNeedsFresh(true);
        setCode('');
      } else if (err instanceof ApiClientError && err.code === 'too_many_attempts') {
        show('code.tooMany');
        setNeedsFresh(true);
        setCode('');
      } else {
        const m = errorMessage(err);
        show(m.key, m.vars);
      }
      inputRef.current?.focus();
    } finally {
      setPending(false);
    }
  }

  async function onResend() {
    if (resending || cooldown > 0 || !email) return;
    setResending(true);
    setError(null);
    setNotice(null);
    try {
      let sent;
      if (needsFresh) {
        sent = await api.requestCode(email, lang);
      } else {
        try {
          sent = await api.resendCode();
        } catch (err) {
          // The challenge died while the page was open (cookie gone or expired): start a new one.
          if (err instanceof ApiClientError && (err.code === 'no_challenge' || err.code === 'code_expired')) sent = await api.requestCode(email, lang);
          else throw err;
        }
      }
      flow.setCooldown(sent.resendAfterSeconds);
      setNeedsFresh(false);
      setCode('');
      setNotice(t('code.resent'));
      inputRef.current?.focus();
    } catch (err) {
      if (session.handleSessionError(err)) return;
      if (err instanceof ApiClientError && err.code === 'rate_limited' && err.retryAfterSeconds) flow.setCooldown(err.retryAfterSeconds);
      const m = errorMessage(err);
      show(m.key, m.vars);
      inputRef.current?.focus();
    } finally {
      setResending(false);
    }
  }

  function changeEmail() {
    // Keep the typed address so the login field is pre-filled; the challenge cookie dies on its own.
    navigate(WEB_ROUTES.login, { state: location.state });
  }

  return (
    <div className="mx-auto w-full max-w-[440px]" data-screen="LoginCodeScreen">
      <PageHeading focus={false}>{t('code.heading')}</PageHeading>
      <p className="text-body text-typography-secondary mt-200 break-words">{email ? t('code.intro', { email }) : t('code.introNoEmail')}</p>
      <Card className="mt-500 p-400 sm:p-500">
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-400">
          <Field
            ref={inputRef}
            label={t('code.label')}
            name="code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            enterKeyHint="go"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            value={code}
            onChange={(ev) => {
              const value = digitsOnly(ev.target.value);
              // Several digits arriving in one change is browser or keyboard autofill of the emailed code: submit it like a paste.
              const filled = value.length - code.length > 1 && normalizeOtpCode(value);
              setCode(value);
              setError(null);
              if (filled) void verify(value);
            }}
            onPaste={onPaste}
            error={error}
            inputClassName="text-h3 text-center font-semibold tracking-[0.35em] tabular-nums"
          />
          <Btn type="submit" size="lg" className="min-h-[48px] w-full" loading={pending}>
            {t('code.submit')}
          </Btn>
        </form>
        <p role="status" aria-live="polite" className="text-callout text-success-text mt-300 empty:hidden">
          {notice}
        </p>
      </Card>

      <div className="mt-500 flex flex-col items-start gap-200">
        {email && (
          <>
            <Btn type="button" variant="secondary" size="lg" className="min-h-[48px]" disabled={cooldown > 0} loading={resending} onClick={() => void onResend()}>
              {t('code.resend')}
            </Btn>
            {cooldown > 0 && <p className="text-callout text-typography-secondary">{t('code.resendIn', { seconds: cooldown })}</p>}
          </>
        )}
        {/* Without an address there is nothing to resend to: the way to a new code is the login screen. */}
        <Btn type="button" variant={email ? 'link' : 'secondary'} size={email ? undefined : 'lg'} className={email ? 'min-h-[44px]' : 'min-h-[48px]'} onClick={changeEmail}>
          {email ? t('code.changeEmail') : t('code.needNewCode')}
        </Btn>
        <p className="text-footnote text-typography-secondary">{t('code.noEmail')}</p>
      </div>
    </div>
  );
}
